import React from "react";
import { formatDateFullYear } from "@/features/common/helper";
import translate from "@/i18n/translate";
import { ProgramHierarchy } from "./MissionAndOutcomesExportView";
import { Lead, LeadInfo } from "@/features/common/interfaces/Interface";
import { OutcomesTerminology } from "@/contexts/AppContext";
import { useFormatMessage } from "@/features/common/helper";

export interface ProgramsTableProps {
  missionHierarchy: ProgramHierarchy[];
  successHierarchy: ProgramHierarchy[];
  learningHierarchy: ProgramHierarchy[];
  iloHierarchy: ProgramHierarchy[];
  curriculumMap: ProgramHierarchy[];
  leads: Lead[];
  outcomesTerminology?: OutcomesTerminology | null;
}

export default function ProgramsTable({
  missionHierarchy,
  successHierarchy,
  learningHierarchy,
  iloHierarchy,
  leads,
  curriculumMap,
  outcomesTerminology
}: ProgramsTableProps) {
  const formatMessage = useFormatMessage();
  const completeHierarchy = {
    missionHierarchy,
    successHierarchy,
    learningHierarchy,
    iloHierarchy,
    curriculumMap
  };
  return (
    <>
      <Table>
        <TableHeader
          columns={[
            translate("app.drilldown.organization"),
            translate("app.orgProgressCard.missionStatement"),
            formatMessage(
              { id: "app.orgProgressCard.learningOutcomes" },
              { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
            ),
            formatMessage(
              { id: "app.orgProgressCard.successOutcomes" },
              { success_outcome_plural: outcomesTerminology?.successOutcomes?.plural || "..." }
            ),
            translate("app.orgProgressCard.curriculumMap"),
            translate("app.orgProgressCard.ILOMapped"),
            translate("app.drilldown.lead")
          ]}
        />

        {missionHierarchy[0].name && (
          <TableBody>
            {Array.isArray(completeHierarchy.missionHierarchy)
              ? completeHierarchy.missionHierarchy.map(
                  (node: ProgramHierarchy, index) => {
                    return (
                      <RenderHierarchy
                        node={{
                          missionHierarchy: node,
                          successHierarchy: successHierarchy[index],
                          learningHierarchy: learningHierarchy[index],
                          iloHierarchy: iloHierarchy[index],
                          curriculumMap: curriculumMap[index]
                        }}
                        leads={leads}
                        outcomesTerminology={outcomesTerminology}
                        key={`${node.name} ${index}`}
                      />
                    );
                  }
                )
              : ""}
          </TableBody>
        )}
      </Table>
    </>
  );
}

const Table = (props: any) => {
  return <table className="data-table -shadow">{props.children}</table>;
};
const TableHeader = (props: any) => {
  return (
    <thead className="dataheader">
      <tr className="dataheaderrow">
        {props.columns.map((column: string, index: number) => {
          return (
            <th
              className="dataheadercell"
              data-th={column}
              key={`column-${index}`}
            >
              {column}
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

const TableBody = (props: any) => {
  return <tbody className="databody">{props.children}</tbody>;
};

const Node = (props: RenderHierarchyProps) => {
  const { node, leads, outcomesTerminology } = props;

  return (
    <>
      {node.missionHierarchy.show_stats ? (
        <CompleteRow node={node} lead={leads[node.missionHierarchy["uuid"]]} outcomesTerminology={outcomesTerminology} />
      ) : (
        <NoStatsRow node={node} />
      )}
    </>
  );
};

interface CompleteRowProps {
  node: completeHierarchy;
  lead: LeadInfo;
  outcomesTerminology?: OutcomesTerminology | null;
}
const CompleteRow = (props: CompleteRowProps) => {
  const { node, lead, outcomesTerminology } = props;
  const formatMessage = useFormatMessage();
  const leadName = () => {
    if (typeof lead != "undefined") {
      return lead.leads.map((l: string, index: number) => {
        return (
          <div key={`lead-${index}-${node.missionHierarchy.uuid}`}>{l}</div>
        );
      });
    }
  };
  return (
    <tr className="datarow" key={`${node.missionHierarchy.uuid}-datarow`}>
      <td className={`datacell level-${node.missionHierarchy.level}`}>
        {node.missionHierarchy.name}
      </td>
      <td className="datacell">
        <StatusCell status={node.missionHierarchy.status} />

        {node.missionHierarchy.updated_at ? (
          <span>{formatDateFullYear(node.missionHierarchy.updated_at)}</span>
        ) : (
          translate("app.na")
        )}
      </td>
      <td className="datacell">
        <StatusCell status={node.learningHierarchy.status} />
        {node.learningHierarchy.name && (
          <span>
            {formatMessage(
              { id: "app.strategy.outcomes" },
              {
                outcome_count: node.learningHierarchy.outcomes,
                outcome_plural_lowercase: outcomesTerminology?.outcomes?.plural?.toLowerCase() || "..."
              }
            )}
          </span>
        )}
      </td>
      <td className="datacell">
        <StatusCell status={node.successHierarchy.status} />
        {node.successHierarchy.name && (
          <span>
            {formatMessage(
              { id: "app.strategy.outcomes" },
              {
                outcome_count: node.successHierarchy.outcomes,
                outcome_plural_lowercase: outcomesTerminology?.outcomes?.plural?.toLowerCase() || "..."
              }
            )}
          </span>
        )}
      </td>
      <td className="datacell">
        <StatusCell status={node.curriculumMap.status} />

        {node.curriculumMap.updated_at ? (
          <span>{formatDateFullYear(node.curriculumMap.updated_at)}</span>
        ) : (
          translate("app.na")
        )}
      </td>
      <td className="datacell">
        <StatusCell status={node.iloHierarchy.status} />
        {node.iloHierarchy.name && (
          <span>
            {formatMessage(
              { id: "app.strategy.mapped" },
              {
                outcomes: node.iloHierarchy.ilo_mapped,
                outcome_plural: outcomesTerminology?.outcomes?.plural || "..."
              }
            )}
          </span>
        )}
      </td>
      <td className="datacell">{leadName()}</td>
    </tr>
  );
};

interface StatusCellProps {
  status: string | undefined;
}
const StatusCell = (props: StatusCellProps) => {
  const content = () => {
    switch (props.status) {
      case "not_started":
        return (
          <div className="not-started-status">
            {translate("app.drilldown.notStarted")}
          </div>
        );
      case "completed":
        return (
          <div className="success-status -completed">
            <div className="title-text">
              {translate("app.drilldown.completed")}
            </div>
          </div>
        );
    }
  };
  return <>{content()}</>;
};

interface NoStatsRowProps {
  node: completeHierarchy;
}
const NoStatsRow = (props: NoStatsRowProps) => {
  const { node } = props;
  return (
    <tr className="datarow" key={`${node.missionHierarchy.uuid}-datarow`}>
      <td className={`datacell level-${node.missionHierarchy.level}`}>
        {node.missionHierarchy.name}
      </td>
      <td className="datacell"></td>
      <td className="datacell"></td>
      <td className="datacell"></td>
      <td className="datacell"></td>
      <td className="datacell"></td>
      <td className="datacell"></td>
    </tr>
  );
};
interface completeHierarchy {
  missionHierarchy: ProgramHierarchy;
  successHierarchy: ProgramHierarchy;
  learningHierarchy: ProgramHierarchy;
  iloHierarchy: ProgramHierarchy;
  curriculumMap: ProgramHierarchy;
}
interface RenderHierarchyProps {
  node: completeHierarchy;
  leads: Lead[];
  outcomesTerminology?: OutcomesTerminology | null;
}
const RenderHierarchy = (props: RenderHierarchyProps) => {
  const { node, leads, outcomesTerminology } = props;
  return (
    <React.Fragment>
      <Node node={node} leads={leads} outcomesTerminology={outcomesTerminology} />
      {node.missionHierarchy.children
        ? node.missionHierarchy.children.map((child: any, index: number) => {
            return (
              <RenderHierarchy
                node={{
                  missionHierarchy: child,
                  successHierarchy: node.successHierarchy.children[index],
                  learningHierarchy: node.learningHierarchy.children[index],
                  iloHierarchy: node.iloHierarchy.children[index],
                  curriculumMap: node.curriculumMap.children[index]
                }}
                leads={leads}
                outcomesTerminology={outcomesTerminology}
                key={`${node.missionHierarchy.name} ${index}-hierarchy`}
              />
            );
          })
        : ""}
    </React.Fragment>
  );
};
